import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';
import { FILM_TIERS, SUBSCRIPTION_STATUS } from '../lib/filmSubscriptionTiers';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up with email/password
  const signUp = async (email, password, firstName, lastName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(result.user, {
        displayName: `${firstName} ${lastName}`
      });

      const now = new Date();

      // Create user document in Firestore
      try {
        await setDoc(doc(db, 'users', result.user.uid), {
          email,
          firstName,
          lastName,
          displayName: `${firstName} ${lastName}`,
          role: 'user',
          createdAt: now,
          lastLogin: now,
          isActive: true,
          filmSubscriptionTier: FILM_TIERS.FREE,
          filmSubscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
          filmSubscriptionStartedAt: now,
          filmSubscriptionExpiresAt: null,
          enterpriseCompanyId: null,
          filmCompanyId: null,
          isEnterpriseAdmin: false,
          isFilmEnterpriseAdmin: false,
          isFilmAdmin: false
        });
      } catch (firestoreError) {
        // If Firestore creation fails, log the error but don't fail the signup
        // The user can still sign in and the document will be created on first sign-in
        console.error('Failed to create Firestore user document:', firestoreError);
        // Re-throw with a more user-friendly message
        throw new Error('Account created but failed to initialize profile. Please try signing in.');
      }

      return result.user;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with email/password
  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user document exists in Firestore
      if (result.user) {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        
        if (!userDoc.exists()) {
          // Create user document if it doesn't exist (for users created before security rule fix)
          const displayName = result.user.displayName || email.split('@')[0];
          const nameParts = displayName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          const now = new Date();
          
          await setDoc(doc(db, 'users', result.user.uid), {
            email: result.user.email || email,
            firstName,
            lastName,
            displayName,
            role: 'user',
            createdAt: now,
            lastLogin: now,
            isActive: true,
            filmSubscriptionTier: FILM_TIERS.FREE,
            filmSubscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
            filmSubscriptionStartedAt: now,
            filmSubscriptionExpiresAt: null,
            enterpriseCompanyId: null,
            filmCompanyId: null,
            isEnterpriseAdmin: false,
            isFilmEnterpriseAdmin: false,
            isFilmAdmin: false
          });
        } else {
          // Update last login
          await updateDoc(doc(db, 'users', result.user.uid), {
            lastLogin: new Date()
          });
        }
      }
      
      return result.user;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        // Create new user document
        const displayName = result.user.displayName || 'Google User';
        const [firstName, ...lastNameParts] = displayName.split(' ');
        const lastName = lastNameParts.join(' ') || '';
        const now = new Date();
        
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          firstName,
          lastName,
          displayName,
          role: 'user',
          createdAt: now,
          lastLogin: now,
          isActive: true,
          authProvider: 'google',
          filmSubscriptionTier: FILM_TIERS.FREE,
          filmSubscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
          filmSubscriptionStartedAt: now,
          filmSubscriptionExpiresAt: null,
          enterpriseCompanyId: null,
          filmCompanyId: null,
          isEnterpriseAdmin: false,
          isFilmEnterpriseAdmin: false,
          isFilmAdmin: false
        });
      } else {
        // Update last login
        await updateDoc(doc(db, 'users', result.user.uid), {
          lastLogin: new Date()
        });
      }
      
      return result.user;
    } catch (error) {
      throw error;
    }
  };

  // Sign out
  const logout = () => {
    return signOut(auth);
  };

  // Check if user is admin
  const isAdmin = () => {
    return userProfile?.role === 'admin' || 
           userProfile?.role === 'super_admin' || 
           userProfile?.email?.includes('admin') || 
           userProfile?.email?.includes('bill.skelly');
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return userProfile?.role === 'super_admin';
  };

  // Check if user is Film Enterprise Admin
  const isEnterpriseAdmin = () => {
    return userProfile?.isEnterpriseAdmin === true || userProfile?.isFilmEnterpriseAdmin === true;
  };

  const isFilmEnterpriseAdmin = () => isEnterpriseAdmin();

  // Check if user is Film Admin
  const isFilmAdmin = () => {
    return userProfile?.isFilmAdmin === true;
  };

  // Get user profile from Firestore with retry logic
  const fetchUserProfile = async (uid, retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Add timeout wrapper to prevent hanging on network issues
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });
        
        const userDoc = await Promise.race([
          getDoc(doc(db, 'users', uid)),
          timeoutPromise
        ]);
        
        if (userDoc.exists()) {
          setUserProfile({ id: userDoc.id, ...userDoc.data() });
          return; // Success, exit retry loop
        }
        return; // Document doesn't exist, no need to retry
      } catch (error) {
        // Suppress non-critical network timeout errors (QUIC protocol errors)
        // These are often transient network issues that don't break functionality
        // Note: Browser-level QUIC errors may still appear in console but don't affect functionality
        const isNetworkTimeout = error?.code === 'unavailable' || 
                                  error?.message?.includes('QUIC') ||
                                  error?.message?.includes('network') ||
                                  error?.message?.includes('timeout') ||
                                  error?.message === 'Request timeout';
        
        if (attempt === retries) {
          // Last attempt failed - only log if it's not a network timeout
          if (!isNetworkTimeout) {
            console.warn('Failed to fetch user profile after retries:', error);
          }
          // Silently handle network timeouts - they're often transient and don't affect functionality
          // The app will continue to work without the user profile (subscription checks will use defaults)
        } else if (!isNetworkTimeout) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        } else {
          // Network timeout - don't retry, just fail silently
          return;
        }
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshUserProfile = async () => {
    if (currentUser?.uid) {
      await fetchUserProfile(currentUser.uid);
    }
  };



  const value = {
    currentUser,
    userProfile,
    refreshUserProfile,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    isAdmin,
    isSuperAdmin,
    isEnterpriseAdmin,
    isFilmEnterpriseAdmin,
    isFilmAdmin,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
